import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "@/content/ContentContext";
import { isVideoUrl } from "./HeroSection";
import AutoVideo from "./AutoVideo";

const EASE = [0.16, 1, 0.3, 1];

export default function EcentraLegacySection() {
  const { content } = useContent();
  const e = content.ecentra;

  return (
    <section id="ecentra-run" className="relative py-24 sm:py-32 overflow-hidden" data-testid="ecentra-run-section">
      <div aria-hidden="true" className="absolute left-[-15%] top-1/2 -translate-y-1/2 w-[45vw] h-[45vw] gold-glow-radial pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.0, ease: EASE }}
            className="lg:col-span-6"
          >
            <p className="font-mono-lux text-xs uppercase tracking-[0.3em] text-gold mb-5" data-testid="ecentra-eyebrow">
              {e.eyebrow}
            </p>
            <h2 className="font-serif-lux text-3xl sm:text-4xl lg:text-[3.4rem] tracking-tight leading-[1.1] text-[#F7F7F8]" data-testid="ecentra-heading">
              {e.h1a} <br />
              <span className="italic text-gold-champagne">{e.h1b}</span> <br />
              {e.h1c}
            </h2>
            <p className="mt-7 text-base sm:text-lg text-[#B8B8C2] leading-relaxed max-w-xl">
              {e.paragraph}
            </p>
            <a
              href={e.ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ecentra-cta-link"
              className="btn-shimmer mt-9 inline-flex items-center gap-2 bg-gold text-[#0A0A0B] font-semibold text-sm tracking-wide px-7 py-3.5 hover:bg-gold-champagne transition-colors duration-300 active:scale-[0.98]"
            >
              {e.cta}
              <ArrowUpRight size={15} />
            </a>
          </motion.div>

          <div className="lg:col-span-6">
            {e.image ? (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9, ease: EASE }}
                className="relative mb-8 overflow-hidden border border-[rgba(212,175,55,0.3)] shadow-[0_0_60px_rgba(212,175,55,0.08)]"
                data-testid="ecentra-image-frame"
              >
                {isVideoUrl(e.image) ? (
                  <AutoVideo
                    src={e.image}
                    className="w-full h-56 sm:h-72 object-cover grayscale brightness-[0.85]"
                    testId="ecentra-image"
                  />
                ) : (
                  <img
                    src={e.image}
                    alt="The Ecentra Run"
                    loading="lazy"
                    className="w-full h-56 sm:h-72 object-cover grayscale brightness-[0.85]"
                    data-testid="ecentra-image"
                  />
                )}
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-[rgba(7,7,8,0.45)] via-transparent to-transparent" />
              </motion.div>
            ) : null}
            <div className="border border-[rgba(212,175,55,0.2)] bg-surface divide-y divide-[rgba(212,175,55,0.14)]">
              {e.highlights.map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, ease: EASE, delay: i * 0.12 }}
                  className="group flex items-start gap-6 p-6 sm:p-7 hover:bg-elevated transition-colors duration-400"
                  data-testid={`ecentra-highlight-${i}`}
                >
                  <span className="font-mono-lux text-xs text-gold tracking-[0.2em] pt-1 w-20 shrink-0 uppercase">{h.k}</span>
                  <span className="gold-rule-v self-stretch" aria-hidden="true" />
                  <p className="text-[15px] text-[#C6C6CF] leading-relaxed">{h.v}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
